/**
 * Hook para estado e carregamento do histórico do processo (TB02130) no modal de detalhes da OS.
 * Usado pelo OrderDetailsModal para a vista "Histórico do processo".
 * OS nativa: carrega também os ciclos (atendimento original + retornos) para a linha do tempo por ciclo.
 */

import { useState, useCallback, useEffect } from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { fetchNativeOrderCycles } from '../services/boardOrderService';

export function useOrderDetailsHistorico(order) {
  const numeroOS = order?.id ?? order?.TB02115_CODIGO;
  const isNative = order?.source === 'native';
  const [showHistoricoView, setShowHistoricoView] = useState(false);
  const [historicoSteps, setHistoricoSteps] = useState([]);
  const [historicoCycles, setHistoricoCycles] = useState(null);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [historicoError, setHistoricoError] = useState(null);

  useEffect(() => {
    setShowHistoricoView(false);
    setHistoricoSteps([]);
    setHistoricoCycles(null);
    setHistoricoError(null);
  }, [numeroOS]);

  const fetchHistorico = useCallback(async () => {
    if (!numeroOS) return;
    setLoadingHistorico(true);
    setHistoricoError(null);
    try {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setHistoricoError('Sessão inválida');
        return;
      }
      const [res, cycles] = await Promise.all([
        fetch(`${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(numeroOS)}/historico-processo`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        isNative ? fetchNativeOrderCycles(numeroOS) : Promise.resolve(null),
      ]);
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success && Array.isArray(json.data)) {
        setHistoricoSteps(json.data);
        setHistoricoCycles(cycles);
      } else {
        setHistoricoError(json.message || 'Erro ao carregar histórico');
        setHistoricoSteps([]);
      }
    } catch (err) {
      setHistoricoError(err.message || 'Erro ao carregar histórico');
      setHistoricoSteps([]);
    } finally {
      setLoadingHistorico(false);
    }
  }, [numeroOS, isNative]);

  const openHistoricoView = useCallback(() => {
    setShowHistoricoView(true);
    fetchHistorico();
  }, [fetchHistorico]);

  const closeHistoricoView = useCallback(() => setShowHistoricoView(false), []);

  return {
    showHistoricoView,
    historicoSteps,
    historicoCycles,
    loadingHistorico,
    historicoError,
    openHistoricoView,
    closeHistoricoView,
  };
}
